import {Sheet, Snackbar, SnackbarProps, Stack} from "@mui/joy";
import {faBomb} from "@fortawesome/pro-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useState
} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {faSkullCrossbones} from "@fortawesome/pro-duotone-svg-icons";
import {errorize} from "./errorize.tsx";

export const TQ_ERROR_LEAK = new Error("tq error leak");

const AbortErrorName = "AbortError";
export const reactRerenderError = "ReactRerenderError";

export function isSpecificError(maybe: Error | undefined, wanted: string): boolean {
    if (!maybe) {
        return false;
    }
    if (maybe.name === wanted) {
        return true;
    }
    if (maybe.message === wanted) {
        return true;
    }
    if (!maybe.cause) {
        return false;
    }
    const cause = maybe.cause as { name: string };
    return cause.name === wanted;
}

export function isAbortError(maybe?: Error) {
    return isSpecificError(maybe, AbortErrorName);
}

export function isRerenderError(maybe?: Error): boolean {
    return isSpecificError(maybe, reactRerenderError);
}

export function Bomb({error: inError, resetErrorBoundary}: {
    error: unknown,
    resetErrorBoundary?: () => void
}) {
    if (!inError) {
        return null;
    }

    const error = errorize(inError);

    return <Snackbar open={true}
                     onClose={() => {
                         if (resetErrorBoundary) {
                             resetErrorBoundary()
                         }
                     }}
                     anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                     size="lg" color="danger" variant="soft">
        <Stack direction="row" spacing={1.8} sx={{alignItems: "center"}}>
            <FontAwesomeIcon icon={faBomb} size="2x"/>
            <Stack direction="column" spacing={1}>
                {error.message}
                {error.cause ? JSON.stringify(error.cause) : null}
            </Stack>
        </Stack>
    </Snackbar>

    // return <Modal open={true}
    //               onClose={() => {
    //                   if (resetErrorBoundary) {
    //                       resetErrorBoundary()
    //                   }
    //               }}
    //               sx={{
    //                   display: "flex", alignItems: "center", justifyContent: "center",
    //                   borderWidth: 4, borderColor: "danger.400", borderStyle: "groove"
    //               }}>
    //     <Sheet sx={{px: 2, py: 4, backgroundColor: "danger.100", color: "danger.600"}}>
    //         <Stack direction="row" spacing={4} sx={{alignItems: "center"}}>
    //             <FontAwesomeIcon icon={faBomb} size="4x"/>
    //             <Stack direction="column" spacing={1}>
    //                 {error.message}
    //                 {error.cause ? JSON.stringify(error.cause) : null}
    //             </Stack>
    //         </Stack>
    //     </Sheet>
    // </Modal>
}

export function RenderBomb({error, resetErrorBoundary}: {
    error: unknown,
    resetErrorBoundary: () => void
}) {
    return <Bomb error={error} resetErrorBoundary={resetErrorBoundary}/>
}

export function HandlerBomb({error, resetErrorBoundary}: {
    error: unknown,
    resetErrorBoundary: () => void
}) {
    return <Bomb error={error} resetErrorBoundary={resetErrorBoundary}/>
}

export interface BombBoxState {
    bomb: (problem: unknown) => void,
}

export const BombBoxContext

    = createContext<BombBoxState>({
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    bomb: () => {
    }
});

export function useBomb(): (problem: unknown) => void {
    const {bomb} = useContext(BombBoxContext);
    return bomb;
}

export function BombProvider({children}: PropsWithChildren) {
    const [bomb, setBomb] = useState<unknown>(null);

    const spySetBomb: Dispatch<SetStateAction<any>> = (next: SetStateAction<any>) => {
        console.log(next);
        setBomb(next);
    }

    return <ErrorBoundary fallbackRender={({error, resetErrorBoundary}) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        <RenderBomb error={error} resetErrorBoundary={resetErrorBoundary}/>}>
        <BombBoxContext value={{bomb: spySetBomb}}>
            {children}
            <HandlerBomb error={bomb} resetErrorBoundary={() => {
                setBomb(null);
            }}/>
        </BombBoxContext>
    </ErrorBoundary>;
}

export type Toast = PropsWithChildren<{
    decos?: Partial<Omit<SnackbarProps, "children" | "anchorOrigin">>;
}>;

export interface SnackbarApi {
    raise: (toast: Toast) => void;
    dismiss: () => void;
}

const ToasterContext = createContext<SnackbarApi>({
    raise: () => {/**/
    },
    dismiss: () => {/**/
    },
});

export function useToaster() {
    return useContext(ToasterContext);
}

export function ToasterProvider({children}: PropsWithChildren) {
    const [toast, setToast] = useState<Toast>();
    const raise = useCallback((toast: Toast) => {
        setToast(toast);
    }, []);
    const dismiss = useCallback(() => {
        setToast(undefined);
    }, []);
    const api
        = useMemo(() => ({raise, dismiss}), [dismiss, raise]);

    const decos = {autoHideDuration: 3000, ...toast?.decos};

    return <ToasterContext value={api}>
        {children}
        {toast
            ? <Snackbar anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                        {...decos} open={!!toast} onClose={() => {
                setToast(undefined);
            }}>
                {toast.children}
            </Snackbar> : null}
    </ToasterContext>;
}

export function Death() {
    return <Sheet color="danger" variant="soft"
                  sx={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%"}}>
        <FontAwesomeIcon size="4x" icon={faSkullCrossbones}/>
    </Sheet>
}

